// Id-keyed catalog of agentic actions the assistant can surface.
// The Coach AI intelligence endpoint returns lightweight action refs (id/kind/
// category); the drawer hydrates each id into a full MessageAction from here.
// Keep these ids in sync with the backend catalog in
// delaget-coach-assistant/src/coach_AI/services/common/action_card_catalog.py.

import {MessageAction, MessageActionKind} from "./intelligenceModels";

export interface ActionCardRef {
    id: string;
    kind: MessageActionKind;
    category: string;
}

interface ActionCatalogEntry {
    kind: MessageActionKind;
    category: string;
    title: string;
    description: string;
    ctaLabel: string;
    result: string;
}

export const actionCatalog: Record<string, ActionCatalogEntry> = {
    launch_loyalty_campaign: {
        kind: "campaign",
        category: "Launch Campaign",
        title: "Launch a loyalty recovery campaign",
        description:
            "Create a Punchh sign-up push for the affected stores with a first-visit bonus to rebuild enrollment.",
        ctaLabel: "Launch Campaign",
        result: 'Drafted a "Double points on sign-up" campaign scoped to the affected stores, scheduled to run the lunch daypart for 14 days. It is queued for your approval in Punchh.'
    },
    adjust_labor_schedule: {
        kind: "schedule",
        category: "Adjust Staffing",
        title: "Adjust the labor schedule",
        description:
            "Propose a labor tweak that adds coverage during the rush at the stores driving the trend.",
        ctaLabel: "Adjust Schedule",
        result: "Built a proposed schedule change adding one associate during the peak rush at the affected stores. Sent to the GMs for confirmation."
    },
    create_speed_alert: {
        kind: "alert",
        category: "Create Alert",
        title: "Create a speed of service alert",
        description:
            "Set an alert that notifies you when drive-thru speed of service slips past target at any location.",
        ctaLabel: "Create Alert",
        result: "Created an alert that watches drive-thru speed of service and notifies you when a location crosses your target threshold. You can manage it from the Agents tab."
    },
    investigate_food_cost: {
        kind: "report",
        category: "Investigate",
        title: "Investigate food cost variance",
        description:
            "Build a focused report on the locations with the highest food cost variance and the likely drivers.",
        ctaLabel: "Investigate",
        result: "Compiled a food cost variance report ranking the locations with the largest theoretical-vs-actual gaps and the likely drivers. It is ready in your reports queue."
    },
    draft_team_comms: {
        kind: "comms",
        category: "Notify Team",
        title: "Draft a note to the team",
        description:
            "Draft an on-brand message to the relevant managers summarizing the finding and the recommended action.",
        ctaLabel: "Notify Team",
        result: "Drafted a concise note to the relevant managers summarizing the finding and the recommended next step. It is ready for your review before sending."
    },
    build_action_plan: {
        kind: "automation",
        category: "Take Action",
        title: "Build a focused action plan",
        description:
            "Turn this into a prioritized, assignable plan for the key locations with owners and due dates.",
        ctaLabel: "Create Action Plan",
        result: "Created a 3-step action plan for the key locations — each step has an owner, a due date, and a check-in after one period. It is ready in your task queue for review."
    },
    schedule_recurring_agent: {
        kind: "schedule",
        category: "Automate",
        title: "Schedule this as a recurring agent",
        description:
            "Re-run this analysis automatically every morning and alert you when the trend moves past target.",
        ctaLabel: "Schedule Agent",
        result: "Scheduled a new agent to re-run this analysis daily at 6:00 AM and notify you when the trend crosses your target threshold. You can manage it from the Agents tab."
    }
};

/** Hydrate lightweight backend action refs into full MessageActions. */
export function hydrateActionCards(refs: ActionCardRef[]): MessageAction[] {
    const actions: MessageAction[] = [];
    refs.forEach((ref, index) => {
        const entry = actionCatalog[ref.id];
        if (!entry) {
            return;
        }
        actions.push({
            id: `${ref.id}_${Date.now()}_${index}`,
            kind: entry.kind,
            category: entry.category,
            title: entry.title,
            description: entry.description,
            ctaLabel: entry.ctaLabel,
            result: entry.result
        });
    });
    return actions;
}
